import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tournamentService } from '../services/api';

const date = v => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
const time = v => new Date(v).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

export default function PublicTournament() {
  const { slug, companySlug } = useParams();
  const targetSlug = slug || companySlug;
  const [data, setData] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!targetSlug) return;
    if (companySlug && !slug) {
      // Company mode: get all tournaments for company (e.g. /arenaxgamer/campeonatos)
      tournamentService.companyPublic(companySlug)
        .then(r => setData({ mode: 'company', company: r.data.company, tournaments: r.data.tournaments }))
        .catch(e => setError(e.response?.data?.error || 'Empresa não encontrada'));
    } else {
      // Single tournament mode (e.g. /campeonato/:slug)
      tournamentService.public(targetSlug)
        .then(r => setData({ mode: 'single', tournament: r.data }))
        .catch(e => setError(e.response?.data?.error || 'Campeonato não encontrado'));
    }
  }, [slug, companySlug, targetSlug]);

  if (error) return (
    <main style={s.center}>
      <h1 style={{ color: '#fff', fontSize: 22 }}>{error}</h1>
      <a style={s.primary} href="/">Voltar para a Arena</a>
    </main>
  );

  if (!data) return <main style={s.center}><div style={{ color: '#a3e635', fontSize: 16, fontWeight: 700 }}>Carregando campeonatos...</div></main>;

  const current = data.mode === 'company'
    ? (data.tournaments[selectedIdx] || data.tournaments[0])
    : data.tournament;

  if (!current) return (
    <main style={s.center}>
      <h1 style={{ color: '#fff', fontSize: 22 }}>Nenhum campeonato ativo no momento</h1>
      <a style={s.primary} href="/">Voltar para a Arena</a>
    </main>
  );

  const rounds = [...new Set((current.matches || []).map(m => m.round_number))];
  const companySlugName = current.company_slug || data.company?.slug || targetSlug;

  return (
    <main style={s.page}>
      <style>{`
        @media(max-width: 920px) {
          .tourn-grid { grid-template-columns: 1fr !important; }
          .tourn-poster { min-height: 440px !important; }
          .prizes-3cols { grid-template-columns: 1fr !important; }
        }
      `}</style>
      
      {/* Header Bar */}
      <header style={s.top}>
        <div style={s.headerInner}>
          {(current.logo_url || data.company?.logo_url) ? (
            <img src={current.logo_url || data.company?.logo_url} alt="" style={s.logo} />
          ) : (
            <span style={s.headerTitle}>{current.company_name || data.company?.name || 'ARENA COMPETITIVA'}</span>
          )}
          <span style={s.tagline}>FEITO DE FÃS PARA FÃS</span>
        </div>
      </header>

      {/* Tabs if company mode has multiple tournaments */}
      {data.mode === 'company' && data.tournaments.length > 1 && (
        <div style={s.tabsBar}>
          {data.tournaments.map((item, idx) => (
            <button
              key={item.id}
              style={{ ...s.tabBtn, ...(idx === selectedIdx ? s.tabBtnActive : {}) }}
              onClick={() => setSelectedIdx(idx)}
            >
              🏆 {item.name}
            </button>
          ))}
        </div>
      )}

      {/* Main Two-Column Layout */}
      <div style={s.container}>
        <div className="tourn-grid" style={s.grid}>

          {/* LEFT COLUMN: Equal Height Poster (9:16) */}
          <div style={s.leftCol}>
            <div className="tourn-poster" style={s.posterWrap}>
              {current.image_url ? (
                <img src={current.image_url} alt={current.name} style={s.posterImg} />
              ) : (
                <div style={s.posterFallback}>
                  <span style={{ fontSize: 50 }}>🎮</span>
                  <strong style={{ fontSize: 20, marginTop: 12, color: '#fff', textAlign: 'center' }}>{current.name}</strong>
                  <small style={{ color: '#a3e635', marginTop: 4, fontWeight: 800 }}>ARTE DE DIVULGAÇÃO 9:16</small>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Equal Height Container with Info, Prizes 3 Cols, Rules and CTA INSIDE */}
          <div style={s.rightCol}>
            {/* Badges */}
            <div style={s.badgeRow}>
              <span style={s.badgeEye}>CAMPEONATO OFICIAL</span>
              <span style={{ ...s.badgeStatus, ...(current.registration_open ? s.badgeStatusOpen : {}) }}>
                {current.registration_open ? '● INSCRIÇÕES ABERTAS' : '○ INSCRIÇÕES ENCERRADAS'}
              </span>
            </div>

            {/* Title & Subtitle */}
            <h1 style={s.mainTitle}>{current.name}</h1>
            <div style={s.gameSub}>{current.game_name} · {current.category_name || 'COMPUTADORES'}</div>

            {current.description && <p style={s.description}>{current.description}</p>}

            {/* Detailed Info Grid */}
            <div style={s.infoGrid}>
              <div style={s.infoItem}>
                <span style={s.infoIcon}>📅</span>
                <div>
                  <small style={s.infoLabel}>PERÍODO DO CAMPEONATO</small>
                  <strong>{date(current.starts_at)}{date(current.starts_at) !== date(current.ends_at) ? ` a ${date(current.ends_at)}` : ''}</strong>
                </div>
              </div>

              <div style={s.infoItem}>
                <span style={s.infoIcon}>🕒</span>
                <div>
                  <small style={s.infoLabel}>HORÁRIO</small>
                  <strong>{time(current.starts_at)} às {time(current.ends_at)}</strong>
                </div>
              </div>

              <div style={s.infoItem}>
                <span style={s.infoIcon}>👥</span>
                <div>
                  <small style={s.infoLabel}>EQUIPES E JOGADORES</small>
                  <strong>{current.team_limit} equipes · {current.players_per_team} jogadores</strong>
                </div>
              </div>

              <div style={s.infoItem}>
                <span style={s.infoIcon}>🎮</span>
                <div>
                  <small style={s.infoLabel}>DISPOSITIVO / CATEGORIA</small>
                  <strong>{current.category_name || 'COMPUTADORES'}</strong>
                </div>
              </div>
            </div>

            {/* MAIN CONTAINER BOX (Containing Fee, 3-Column Prizes, Disputa Rules and CTA Button) */}
            <div style={s.mainBox}>
              {/* Fee Row */}
              <div style={s.feeRow}>
                <span style={s.feeIcon}>💳</span>
                <div>
                  <small style={s.feeLabel}>INSCRIÇÃO POR EQUIPE</small>
                  <strong style={s.feeValue}>R$ {Number(current.registration_fee).toFixed(2).replace('.', ',')}</strong>
                </div>
              </div>

              {/* Premiação Header & 3 Columns */}
              <div style={s.prizesSection}>
                <h3 style={s.prizesTitle}>Premiação</h3>
                <div className="prizes-3cols" style={s.prizes3Cols}>
                  <div style={s.prizeColCard}>
                    <span style={s.medalHeader}>🥇 1º Lugar</span>
                    <strong style={s.prizeVal}>{current.prize_first || 'A definir'}</strong>
                  </div>
                  <div style={s.prizeColCard}>
                    <span style={s.medalHeader}>🥈 2º Lugar</span>
                    <strong style={s.prizeVal}>{current.prize_second || 'A definir'}</strong>
                  </div>
                  <div style={s.prizeColCard}>
                    <span style={s.medalHeader}>🥉 3º Lugar</span>
                    <strong style={s.prizeVal}>{current.prize_third || 'A definir'}</strong>
                  </div>
                </div>
              </div>

              {/* Modelo da Disputa */}
              <div style={s.rulesSection}>
                <h4 style={s.rulesTitle}>MODELO DA DISPUTA</h4>
                <div style={s.rulesRow}>
                  <span>Primeiras fases <b>MD{current.early_best_of}</b></span>
                  <span>Semifinal <b>MD{current.semifinal_best_of}</b></span>
                  <span>Final <b>MD{current.final_best_of}</b></span>
                  <span>Terceiro lugar <b>{current.third_place_enabled ? `MD${current.third_place_best_of}` : 'Não'}</b></span>
                </div>
              </div>

              {/* CTA Button INSIDE THE BOX */}
              <div style={s.ctaWrapInside}>
                {current.registration_open ? (
                  <a
                    style={s.ctaBtn}
                    href={`/cliente/${companySlugName}?campeonato=${encodeURIComponent(current.slug)}`}
                  >
                    ⚡ ENTRAR E INSCREVER EQUIPE
                  </a>
                ) : (
                  <div style={s.ctaClosed}>
                    {current.status === 'draft' ? 'Inscrições serão abertas em breve' : 'Inscrições encerradas ou vagas preenchidas'}
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>

        {/* Bottom Sections: Confirmed Teams & Bracket */}
        {(current.teams || []).length > 0 && (
          <div style={{ ...s.card, marginTop: 24 }}>
            <h3 style={s.cardTitle}>Equipes confirmadas ({current.teams.length}/{current.team_limit})</h3>
            <div style={s.teamsGrid}>
              {current.teams.map(x => (
                <div key={x.id} style={s.teamCard}>
                  {x.clan_logo ? <img src={x.clan_logo} alt="" style={s.clanLogo} /> : <i style={{ ...s.clanFallback, background: x.clan_color || '#a3e635' }}>{x.name[0]}</i>}
                  <div>
                    <strong>{x.name}</strong>
                    <small style={{ display: 'block', color: '#8b8b9e', fontSize: 11 }}>{x.clan_name || 'Equipe independente'}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {rounds.length > 0 && (
          <div style={{ ...s.card, marginTop: 24 }}>
            <h3 style={s.cardTitle}>Chaveamento do Campeonato</h3>
            <div style={s.bracketWrap}>
              {rounds.map(r => (
                <div key={r} style={s.roundCol}>
                  <h4 style={s.roundName}>{current.matches.find(m => m.round_number === r)?.round_name}</h4>
                  {current.matches.filter(m => m.round_number === r).map(m => (
                    <div key={m.id} style={s.matchBox}>
                      <small style={{ color: '#8b8b9e', fontSize: 10 }}>
                        {m.scheduled_start ? `${date(m.scheduled_start)} · ${time(m.scheduled_start)}` : 'Data a definir'} · MD{m.best_of}
                      </small>
                      <div style={s.matchTeam}>
                        <span>{m.team1_name || 'A definir'}</span>
                        <b>{m.team1_score}</b>
                      </div>
                      <div style={s.matchTeam}>
                        <span>{m.team2_name || 'A definir'}</span>
                        <b>{m.team2_score}</b>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#08080d', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#08080d', color: '#fff', gap: 14 },
  top: { height: 60, background: '#0c0c14', borderBottom: '1px solid #202030', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' },
  headerInner: { display: 'flex', alignItems: 'center', gap: 14 },
  logo: { height: 38, maxWidth: 150, objectFit: 'contain' },
  headerTitle: { fontSize: 16, fontWeight: 900, letterSpacing: 1, color: '#fff' },
  tagline: { fontSize: 10, fontWeight: 900, color: '#a3e635', letterSpacing: 1.5, textTransform: 'uppercase', background: '#172808', padding: '3px 9px', borderRadius: 16 },
  tabsBar: { display: 'flex', gap: 8, justifyContent: 'center', padding: '12px 16px', background: '#101018', borderBottom: '1px solid #202030', overflowX: 'auto' },
  tabBtn: { padding: '8px 14px', background: '#181824', color: '#a1a1b2', border: '1px solid #2d2d3f', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' },
  tabBtnActive: { background: '#a3e635', color: '#08080d', border: '1px solid #a3e635' },
  container: { maxWidth: 1240, margin: '0 auto', padding: '24px 16px 60px' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 24, alignItems: 'stretch' },
  leftCol: { display: 'flex', flexDirection: 'column', height: '100%' },
  rightCol: { display: 'flex', flexDirection: 'column', height: '100%', gap: 10 },
  
  /* Equal Height Poster */
  posterWrap: { width: '100%', height: '100%', minHeight: 480, background: '#12121c', borderRadius: 16, border: '1px solid #2a2a3e', overflow: 'hidden', boxShadow: '0 14px 36px rgba(0,0,0,0.5)', position: 'relative', display: 'flex' },
  posterImg: { width: '100%', height: '100%', objectFit: 'cover' },
  posterFallback: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'linear-gradient(180deg, #161624, #0c0c14)' },

  /* Badges & Titles */
  badgeRow: { display: 'flex', gap: 10, alignItems: 'center' },
  badgeEye: { fontSize: 10, fontWeight: 900, color: '#a3e635', letterSpacing: 1.2, textTransform: 'uppercase' },
  badgeStatus: { fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 16, background: '#2d151c', color: '#fb7185' },
  badgeStatusOpen: { background: '#1c330c', color: '#bef264' },
  mainTitle: { fontSize: 'clamp(24px, 3.2vw, 36px)', margin: '2px 0', color: '#fff', fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.05 },
  gameSub: { fontSize: 14, fontWeight: 800, color: '#a3e635', margin: '0 0 6px' },
  description: { color: '#a6a6b8', fontSize: 13, lineHeight: 1.4, margin: '4px 0 10px' },

  /* Detailed Info Grid */
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, margin: '6px 0 10px' },
  infoItem: { background: '#11111b', border: '1px solid #232334', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 },
  infoIcon: { fontSize: 20 },
  infoLabel: { display: 'block', fontSize: 9, fontWeight: 800, color: '#7a7a8c', letterSpacing: 0.8, marginBottom: 2 },

  /* Main Container Box */
  mainBox: { background: 'linear-gradient(180deg, #121c10, #0e141a)', border: '1px solid #254012', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 16, flex: 1, justifyContent: 'space-between' },
  feeRow: { display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #203510', paddingBottom: 12 },
  feeIcon: { fontSize: 24 },
  feeLabel: { display: 'block', fontSize: 10, fontWeight: 900, color: '#7a7a8c', letterSpacing: 0.8 },
  feeValue: { color: '#a3e635', fontSize: 22, fontWeight: 900 },

  /* Premiação em 3 Colunas */
  prizesSection: { display: 'flex', flexDirection: 'column', gap: 10 },
  prizesTitle: { margin: 0, fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: 0.5 },
  prizes3Cols: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  prizeColCard: { background: '#0a1107', border: '1px solid #203a0e', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'center' },
  medalHeader: { fontSize: 12, fontWeight: 900, color: '#a3e635' },
  prizeVal: { fontSize: 14, fontWeight: 800, color: '#fff' },

  /* Modelo da Disputa */
  rulesSection: { borderTop: '1px solid #203510', paddingTop: 12 },
  rulesTitle: { margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: '#7a7a8c', textTransform: 'uppercase', letterSpacing: 0.8 },
  rulesRow: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 12, color: '#b5b5c8' },

  /* CTA Button INSIDE THE BOX */
  ctaWrapInside: { marginTop: 4 },
  ctaBtn: { display: 'block', width: '100%', padding: '15px 20px', background: '#a3e635', color: '#08080d', textAlign: 'center', textDecoration: 'none', fontWeight: 900, fontSize: 15, letterSpacing: 0.8, borderRadius: 10, boxShadow: '0 6px 24px rgba(163, 230, 53, 0.28)', transition: 'all 0.2s', boxSizing: 'border-box' },
  ctaClosed: { padding: '14px', background: '#1a1a26', border: '1px solid #2c2c3e', borderRadius: 10, color: '#8e8ea0', textAlign: 'center', fontWeight: 700, fontSize: 13 },

  /* Cards */
  card: { background: '#12121e', border: '1px solid #252536', borderRadius: 14, padding: 18 },
  cardTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: 0.5 },

  /* Teams & Bracket */
  teamsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 },
  teamCard: { display: 'flex', alignItems: 'center', gap: 10, background: '#0c0c14', border: '1px solid #222234', padding: '9px 12px', borderRadius: 9 },
  clanLogo: { width: 30, height: 30, objectFit: 'contain', borderRadius: 5 },
  clanFallback: { width: 30, height: 30, borderRadius: 5, display: 'grid', placeItems: 'center', fontWeight: 900, color: '#000', fontSize: 14 },
  bracketWrap: { display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 6 },
  roundCol: { minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 },
  roundName: { margin: '0 0 4px', color: '#a3e635', fontSize: 13, fontWeight: 800 },
  matchBox: { background: '#0a0a10', border: '1px solid #222232', padding: 10, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 5 },
  matchTeam: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#fff' },
};
